/**
 * Hydra Modulate Node Definitions
 *
 * Two-input modulation operations: modulate, modulateHue, modulateRotate, etc.
 * Second input is used to modulate the first input's parameters.
 */


import { HydraCompositeNode } from './HydraCompositeNode';

import type { NodeDef } from '@runtime/IRuntime';
import { internals, params } from '@/nodes/TypedEndpoints';
import { Endpoint } from '@nodes/Endpoint';
import { Dependencies } from '@/nodes/Dependencies';

const modulateInOut = internals({
  hydraIn1: { dataType: 'hydra', isInput: true, label: 'Source' },
  hydraIn2: { dataType: 'hydra', isInput: true, label: 'Modulator' },
  hydraOut: { dataType: 'hydra', isOutput: true },
})

export interface HydraConfig {
  type: string;
  shortName: string;
  hydraFn: string;
  params: Record<string, Partial<Endpoint>>;
  icon?: string;
  info?: string;
}
const modulateConfigs: HydraConfig[] = [
  {
    type: 'Modulate',
    shortName: 'mod',
    hydraFn: 'modulate',
    params: {
      amount: { value: 0.1, min: 0, max: 1, step: 0.01 },
    }
  },
  {
    type: 'ModulateHue',
    shortName: 'modHue',
    hydraFn: 'modulateHue',
    params: {
      amount: { value: 0.1, min: 0, max: 1, step: 0.01 },
    }
  },
  {
    type: 'ModulateRotate',
    shortName: 'modRotate',
    hydraFn: 'modulateRotate',
    params: {
      multiple: { value: 1, min: 0, max: 10, step: 0.1 },
      offset: { value: 0, min: -3.14, max: 3.14, step: 0.01 },
    }
  },
  {
    type: 'ModulateKaleid',
    shortName: 'modKaleid',
    hydraFn: 'modulateKaleid',
    params: {
      nSides: { value: 4, min: 1, max: 50, step: 1 },
    }
  },
  {
    type: 'ModulatePixelate',
    shortName: 'modPixel',
    hydraFn: 'modulatePixelate',
    params: {
      multiple: { value: 10, min: 1, max: 100, step: 1 },
      offset: { value: 3, min: 0, max: 20, step: 0.1 },
    }
  },
  {
    type: 'ModulateRepeat',
    shortName: 'modRepeat',
    hydraFn: 'modulateRepeat',
    params: {
      repeatX: { value: 3, min: 1, max: 20, step: 1 },
      repeatY: { value: 3, min: 1, max: 20, step: 1 },
      offsetX: { value: 0.5, min: 0, max: 2, step: 0.01 },
      offsetY: { value: 0.5, min: 0, max: 2, step: 0.01 },
    }
  },
  {
    type: 'ModulateScale',
    shortName: 'modScale',
    hydraFn: 'modulateScale',
    params: {
      multiple: { value: 1, min: 0.01, max: 10, step: 0.01 },
      offset: { value: 1, min: 0, max: 10, step: 0.01 },
    }
  },
  {
    type: 'ModulateScrollX',
    shortName: 'modScrollX',
    hydraFn: 'modulateScrollX',
    params: {
      scrollX: { value: 0.5, min: -2, max: 2, step: 0.01 },
      speed: { value: 0, min: -2, max: 2, step: 0.01 }
    }
  },
  {
    type: 'ModulateScrollY',
    shortName: 'modScrollY',
    hydraFn: 'modulateScrollY',
    params: {
      scrollY: { value: 0.5, min: -2, max: 2, step: 0.01 },
      speed: { value: 0, min: -2, max: 2, step: 0.01 },
    }
  }
];

// Generate node definitions from configs
export const modulateNodes: NodeDef[] = modulateConfigs.map(config => {
  const endpoints = {
    ...params(config.params),
    ...modulateInOut
  }
return {
  type: config.type,
  shortName: config.shortName,
  category: 'modulate',
  endpoints,
  ctor: class extends HydraCompositeNode<typeof endpoints> {
    constructor(id: string, deps: Dependencies) {
      super(id, deps, { 
        hydraFn: config.hydraFn, 
        fnArgs: Object.keys(config.params)
      });
    }
  }
}
});
